<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoneyValue } from '../utils/format'
import type { ExpenseReference } from '../types/finance'

/**
 * Overhead picker backed by the `expense_type` reference catalogue
 * (`GET /reference-items/expense_type`). The endpoint returns the whole list,
 * so search and paging are client-side like the counterparty picker.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    selectedId?: string | number | null
  }>(),
  { selectedId: null },
)

const emit = defineEmits<{
  close: []
  select: [expense: ExpenseReference]
}>()

const { t } = useI18n()
const { listExpenseReferences } = useFinanceApi()

const PAGE_SIZE = 20

const searchValue = ref('')
const expenses = ref<ExpenseReference[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)

const filtered = computed<SmartPickerOption[]>(() => {
  const search = searchValue.value.trim().toLowerCase()
  return expenses.value
    .filter(
      (expense) =>
        !search ||
        [expense.name, expense.code].some((value) =>
          String(value ?? '').toLowerCase().includes(search),
        ),
    )
    .map((expense) => ({
      id: expense.id,
      title: expense.name,
      subtitle: expense.code || undefined,
      meta:
        expense.base_amount != null
          ? `${formatMoneyValue(expense.base_amount)} ${expense.default_rate_currency_code ?? ''}`.trim()
          : undefined,
    }))
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)))

const paged = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filtered.value.slice(start, start + PAGE_SIZE)
})

const rangeLabel = computed(() => {
  const total = filtered.value.length
  const from = total === 0 ? 0 : (page.value - 1) * PAGE_SIZE + 1
  const to = Math.min(page.value * PAGE_SIZE, total)
  return t('finance.picker.pageRange', { from, to, total })
})

watch(searchValue, () => {
  page.value = 1
})

watch(totalPages, (value) => {
  if (page.value > value) page.value = value
})

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
}

function jumpToSelection() {
  const selected = props.selectedId
  if (selected === null || selected === undefined) return
  const index = filtered.value.findIndex((option) => String(option.id) === String(selected))
  if (index >= 0) page.value = Math.floor(index / PAGE_SIZE) + 1
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    expenses.value = await listExpenseReferences()
    jumpToSelection()
  } catch (cause) {
    error.value = formatApiError(cause)
    expenses.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    searchValue.value = ''
    page.value = 1
    void load()
  },
)

onMounted(() => {
  if (props.open) void load()
})

function selectOption(option: SmartPickerOption) {
  const match = expenses.value.find((expense) => String(expense.id) === String(option.id))
  if (match) emit('select', match)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="t('finance.picker.expenseTitle')"
    :eyebrow="t('finance.picker.expenseEyebrow')"
    :search-label="t('finance.picker.expenseSearch')"
    :search-placeholder="t('finance.picker.expensePlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="t('finance.picker.expenseEmpty')"
    :items="paged"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="rangeLabel"
    :close-aria-label="t('finance.picker.close')"
    :retry-label="t('finance.picker.retry')"
    :previous-label="t('finance.picker.previous')"
    :next-label="t('finance.picker.next')"
    :cancel-label="t('finance.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="goToPage(page - 1)"
    @next="goToPage(page + 1)"
    @select="selectOption"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ t('finance.picker.expenseLoading') }}</template>
  </SmartPickerModal>
</template>
